// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { isValid } from 'date-fns'
import { Box, Chip, Grid, IconButton, Stack, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomModal } from '../common/CustomModal'
import { InputFormFields } from './InputFormFields'
// others
import { ExternalUser, Maybe, UpdateExternalUserInput, useUpdateExternalUserMutation } from '../../../generated'
import { GENERAL_INFORMATION, NOT_APPLICABLE } from '../../constants'
import { AppContext } from '../../context/AppContext'
import { ActionType } from '../../context/AppContextReducer'
import { ErrorException } from '../../interfaceTypes'
import { capitalizeCase } from '../../utils'
import { updateJudgeValidationSchema } from '../../validationSchema'
import { CommonExternalUserView } from '../common/CommonExternalUserView'
import { ExternalUserActionType } from './externalUserReducer'
import { EditJudgeProps } from '../../interfaceTypes/props'

export const EditJudge: FC<EditJudgeProps> = ({
  userDetails,
  state,
  dispatch: externalUserDispatch,
  refetch,
  isEdit: defaultIsEdit
}): JSX.Element => {
  const { dispatch, isEdit } = useContext(AppContext)
  const { currentExternalUser } = state || {}
  const userDetailTest = userDetails && userDetails
  const [userDetail, setUserDetail] = useState<Maybe<ExternalUser>>(userDetailTest!)

  const {
    firstName,
    middleName,
    suffix,
    initials,
    dateOfBirth,
    lastName,
    email,
    phoneNumber,
    clientNumber,
    id,
    phoneNumberExtension,
    courtToExternalUserToExternalUserRole
  } = userDetail || currentExternalUser || {}
  const courts = courtToExternalUserToExternalUserRole?.map(item => item?.court)
  const [isEditOpen, setIsEditOpen] = useState<boolean>(defaultIsEdit ?? false)
  const phoneNumberWithOutCountryCode = phoneNumber?.at(0) === '+' ? phoneNumber?.substring(2) : phoneNumber

  useEffect(() => {
    setUserDetail(userDetailTest!)
    // eslint-disable-next-line
  }, [userDetails])

  const handleClose = () => {
    if (isEdit) {
      dispatch({ type: ActionType.SET_IS_EDIT, isEdit: !isEdit })
    }
    setIsEditOpen(false)
    refetch && refetch()
  }

  const [updateExternalUserMutation, { loading: updateExternalUserLoader }] = useUpdateExternalUserMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          }
        )
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          updateExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success('External user updated successfully')
            externalUserDispatch &&
              externalUserDispatch({
                type: ExternalUserActionType.SET_CURRENT_EXTERNAL_USER,
                currentExternalUser: externalUser || []
              })
            resetFields()
            setIsEditOpen(false)
            setUserDetail(externalUser)
          }
        }
      }
    }
  })

  const methods = useForm<UpdateExternalUserInput>({
    mode: 'all',
    resolver: yupResolver(updateJudgeValidationSchema)
  })

  const { handleSubmit, reset, watch } = methods

  const resetFields = useCallback(() => {
    reset({
      firstName: firstName || '',
      middleName: middleName || '',
      suffix: suffix || '',
      initials: initials || '',
      dateOfBirth: dateOfBirth || '',
      lastName: lastName || '',
      phoneNumber: phoneNumberWithOutCountryCode || '',
      email: email || '',
      clientNumber: clientNumber || '',
      contractBalance: undefined,
      phoneNumberExtension: phoneNumberExtension || ''
    })
  }, [
    reset,
    firstName,
    middleName,
    suffix,
    initials,
    dateOfBirth,
    lastName,
    phoneNumberWithOutCountryCode,
    email,
    clientNumber,
    phoneNumberExtension
  ])

  useEffect(() => {
    resetFields()
  }, [firstName, lastName, phoneNumber, email, phoneNumberWithOutCountryCode, resetFields])

  const onSubmit: SubmitHandler<UpdateExternalUserInput> = async (data: UpdateExternalUserInput) => {
    const { firstName, lastName, phoneNumber, dateOfBirth, ...rest } = data

    await updateExternalUserMutation({
      variables: {
        updateExternalUserInput: {
          ...rest,
          id: id || '',
          firstName: firstName?.trim() || '',
          lastName: lastName?.trim() || '',
          phoneNumber: phoneNumber ?? undefined,
          dateOfBirth: isValid(new Date(dateOfBirth)) ? dateOfBirth : undefined
        }
      }
    })
  }

  return (
    <CustomModal title="Judge Details" isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" style={{ alignItems: 'baseline' }}>
            <Typography variant="body2" fontSize="18px" sx={{ fontWeight: 'medium', marginBottom: '0.875rem' }}>
              {GENERAL_INFORMATION}
            </Typography>

            <Box style={{ alignItems: 'baseline' }}>
              <IconButton
                sx={{ p: 0 }}
                color="primary"
                size="large"
                aria-label={isEditOpen ? 'back' : 'edit'}
                onClick={() => {
                  setIsEditOpen(!isEditOpen)
                  resetFields()
                }}
              >
                {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
              </IconButton>

              {isEditOpen && (
                <IconButton color="primary" type="submit" disabled={updateExternalUserLoader} aria-label="save">
                  <SaveIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box marginTop={2}>
            <Collapse in={!isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <CommonExternalUserView externalUser={(userDetail ?? currentExternalUser) as ExternalUser} isJudge />
              </Grid>

              <Typography
                variant="body2"
                fontSize="18px"
                sx={{ fontWeight: 'medium', marginBottom: '0.875rem', marginTop: '20px' }}
              >
                Courts:
              </Typography>

              {courts?.length === 0 ? (
                <Typography>{NOT_APPLICABLE}</Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap="10px">
                  {courts?.map(assignedCourt => {
                    const { name } = assignedCourt || {}

                    return <Chip key={`${id}-${firstName}`} label={capitalizeCase(name || '')} variant="outlined" />
                  })}
                </Stack>
              )}
            </Collapse>

            <Collapse in={isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <InputFormFields
                  isJudge
                  showExtension={watch('phoneNumber') && (watch('phoneNumber')?.length ?? 0) >= 10}
                />
              </Grid>
            </Collapse>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
